import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";

import type { Mach1Shard, Mach1WebManifest } from "./mach1-model";

export const EXACT_MANIFEST_SHA256 =
  "9f068429675c3e92b0d6fa39cd177a487675487bfcc9532d746aaf6c1f1c5921";
const INSTALL_ROOT = `prime-mach1/${EXACT_MANIFEST_SHA256}`;
const MINIMUM_FREE_BYTES = 512 * 1024 * 1024;
const DOWNLOAD_RANGE_BYTES = 32 * 1024 * 1024;
const DOWNLOAD_RANGE_ATTEMPTS = 5;
const RANGE_DIRECTORY = ".ranges";

export interface ModelInstallProgress {
  phase: "inventory" | "preflight" | "download" | "assemble" | "verify" | "complete";
  networkActive: boolean;
  shardIndex: number;
  shardCount: number;
  shardBytes: number;
  shardTotalBytes: number;
  totalBytes: number;
  totalExpectedBytes: number;
  detail: string;
}

export interface ExactMach1InstallationFiles {
  manifest: Mach1WebManifest;
  files: File[];
  manifestSha256: typeof EXACT_MANIFEST_SHA256;
  accounting: {
    downloadedByteCount: number;
    reusedVerifiedByteCount: number;
    duplicateCopyByteCount: 0;
    verifiedFileNames: string[];
  };
}

export interface VerifiedMach1Installation extends ExactMach1InstallationFiles {
  verification: "site-sha256";
}

/** Fresh OPFS snapshots that the frozen exact runtime must SHA-verify before use. */
export interface RuntimeVerificationMach1Installation extends ExactMach1InstallationFiles {
  verification: "deferred-to-exact-runtime";
}

export interface ExactMach1StorageStatus {
  persisted: boolean;
  usage: number | null;
  quota: number | null;
}

type ProgressCallback = (progress: ModelInstallProgress) => void;

function formatProgressBytes(bytes: number): string {
  if (bytes >= 2 ** 30) return `${(bytes / 2 ** 30).toFixed(2)} GiB`;
  return `${(bytes / 2 ** 20).toFixed(1)} MiB`;
}

function isNotFoundError(error: unknown): boolean {
  return Boolean(
    error &&
    typeof error === "object" &&
    "name" in error &&
    error.name === "NotFoundError",
  );
}

function incompleteInstallation(objectName: string): Error {
  return new Error(
    `The device-local MACH-1 installation is incomplete: ${objectName} is missing. ` +
    "Tap Install or verify to recheck the 18 shards and download only missing bytes.",
  );
}

function retryDelay(attempt: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, Math.min(4_000, 250 * 2 ** attempt)));
}

async function fetchVerifiedRangeBytes(
  shard: Mach1Shard,
  offset: number,
  length: number,
  signal?: AbortSignal,
): Promise<Uint8Array> {
  const end = offset + length - 1;
  let lastError: unknown;
  for (let attempt = 0; attempt < DOWNLOAD_RANGE_ATTEMPTS; attempt += 1) {
    signal?.throwIfAborted();
    try {
      const response = await fetch(`/api/model/shards/${encodeURIComponent(shard.file)}`, {
        cache: "no-store",
        credentials: "same-origin",
        headers: { range: `bytes=${offset}-${end}` },
        signal,
      });
      if (
        response.status !== 206 ||
        Number(response.headers.get("content-length")) !== length ||
        response.headers.get("content-range") !== `bytes ${offset}-${end}/${shard.byteCount}` ||
        response.headers.get("x-mach1-sha256") !== shard.sha256
      ) {
        throw new Error(`Server did not provide exact range ${offset}-${end} for shard ${shard.index}`);
      }
      const bytes = new Uint8Array(await response.arrayBuffer());
      if (bytes.byteLength !== length) {
        throw new Error(`Shard ${shard.index} range ${offset}-${end} ended early`);
      }
      return bytes;
    } catch (error) {
      if (signal?.aborted) throw error;
      lastError = error;
      if (attempt + 1 < DOWNLOAD_RANGE_ATTEMPTS) await retryDelay(attempt);
    }
  }
  throw lastError instanceof Error ? lastError : new Error(`Shard ${shard.index} range download failed`);
}

function assertBrowserStorage(): void {
  if (
    typeof navigator === "undefined" ||
    !navigator.storage ||
    typeof navigator.storage.getDirectory !== "function"
  ) {
    throw new Error("This browser does not expose the Origin Private File System required by exact MACH-1");
  }
}

function assertManifest(manifest: Mach1WebManifest): void {
  if (
    manifest.schemaVersion !== 1 ||
    manifest.architecture !== "mach1" ||
    manifest.formatVersion !== 3 ||
    manifest.tensorCount !== 1_637 ||
    manifest.source.revision !== "bcbf113f7a9be509adc8fc9cbc49393a9f592485" ||
    manifest.unsplit.byteCount !== 7_872_751_712 ||
    manifest.unsplit.sha256 !== "225c7f283c78e8990309cad9c9dddb17d928124cc071a82ec993e79880c03bb2" ||
    manifest.chatTemplate.sha256 !== "e84f32a23fdda27689f868aa4a1a5621f41133e51a48d7f3efcbea2839574259" ||
    manifest.splitter.revision !== "fa68073841ae1b08b69194ec0371966352482e6c" ||
    manifest.tensorIndex.sha256 !== "469206f990e5345e8670b2613d60f0ec007d6f7b180280d2922c8dec114e2383" ||
    manifest.split.shardCount !== 18 ||
    manifest.split.shards.length !== 18 ||
    manifest.split.shards.reduce((sum, shard) => sum + shard.byteCount, 0) !== manifest.split.totalByteCount
  ) {
    throw new Error("Downloaded MACH-1 manifest identity is not the pinned release");
  }
}

export async function fetchExactMach1Manifest(signal?: AbortSignal): Promise<Mach1WebManifest> {
  const response = await fetch("/model/manifest.json", {
    cache: "no-cache",
    credentials: "same-origin",
    signal,
  });
  if (!response.ok) throw new Error(`MACH-1 manifest request failed (${response.status})`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  const digest = bytesToHex(sha256(bytes));
  if (digest !== EXACT_MANIFEST_SHA256) {
    throw new Error(`MACH-1 manifest SHA-256 mismatch: ${digest}`);
  }
  const manifest = JSON.parse(new TextDecoder().decode(bytes)) as Mach1WebManifest;
  assertManifest(manifest);
  return manifest;
}

async function installDirectory(create: boolean): Promise<FileSystemDirectoryHandle> {
  assertBrowserStorage();
  let directory = await navigator.storage.getDirectory();
  for (const segment of INSTALL_ROOT.split("/")) {
    try {
      directory = await directory.getDirectoryHandle(segment, { create });
    } catch (error) {
      if (!create && isNotFoundError(error)) throw incompleteInstallation(INSTALL_ROOT);
      throw error;
    }
  }
  return directory;
}

export async function deleteExactMach1Installation(): Promise<boolean> {
  assertBrowserStorage();
  const root = await navigator.storage.getDirectory();
  const namespace = await root
    .getDirectoryHandle("prime-mach1")
    .catch((error: unknown) => {
      if (isNotFoundError(error)) return null;
      throw error;
    });
  if (!namespace) return false;

  try {
    await namespace.removeEntry(EXACT_MANIFEST_SHA256, { recursive: true });
    return true;
  } catch (error) {
    if (isNotFoundError(error)) return false;
    throw error;
  }
}

async function removeInstallMarker(directory: FileSystemDirectoryHandle): Promise<void> {
  try {
    await directory.removeEntry("install.json");
  } catch (error) {
    if (!isNotFoundError(error)) throw error;
  }
}

async function requireStoredFile(
  directory: FileSystemDirectoryHandle,
  name: string,
): Promise<File> {
  try {
    return await directory.getFileHandle(name).then((handle) => handle.getFile());
  } catch (error) {
    if (isNotFoundError(error)) throw incompleteInstallation(name);
    throw error;
  }
}

async function requireStorageCapacity(requiredBytes: number): Promise<void> {
  assertBrowserStorage();
  await navigator.storage.persist?.();
  const estimate = await navigator.storage.estimate();
  if (
    typeof estimate.quota === "number" &&
    typeof estimate.usage === "number" &&
    estimate.quota - estimate.usage < requiredBytes + MINIMUM_FREE_BYTES
  ) {
    throw new Error(
      `Browser storage needs at least ${Math.ceil((requiredBytes + MINIMUM_FREE_BYTES) / 2 ** 30)} GiB free`,
    );
  }
}

export async function getExactMach1StorageStatus(
  requestPersistence = false,
): Promise<ExactMach1StorageStatus> {
  assertBrowserStorage();
  if (requestPersistence) await navigator.storage.persist?.();
  const [persisted, estimate] = await Promise.all([
    navigator.storage.persisted?.() ?? Promise.resolve(false),
    navigator.storage.estimate(),
  ]);
  return {
    persisted,
    usage: typeof estimate.usage === "number" ? estimate.usage : null,
    quota: typeof estimate.quota === "number" ? estimate.quota : null,
  };
}

async function hashBlob(
  blob: Blob,
  onChunk?: (readBytes: number) => void,
  signal?: AbortSignal,
): Promise<string> {
  const hash = sha256.create();
  const reader = blob.stream().getReader();
  let offset = 0;
  try {
    for (;;) {
      signal?.throwIfAborted();
      const { value, done } = await reader.read();
      if (done) break;
      hash.update(value);
      offset += value.byteLength;
      onChunk?.(offset);
    }
    return bytesToHex(hash.digest());
  } finally {
    reader.releaseLock();
  }
}

interface ExistingShardInventory {
  validFiles: Set<string>;
  partialBytes: Map<string, number>;
  validBytes: number;
  savedBytes: number;
  missingBytes: number;
  needsRepair: boolean;
}

function shardRangeDirectoryName(shard: Mach1Shard): string {
  return `shard-${String(shard.index).padStart(2, "0")}`;
}

function rangeFileName(offset: number): string {
  return `${String(offset).padStart(12, "0")}.range`;
}

async function shardRangeDirectory(
  directory: FileSystemDirectoryHandle,
  shard: Mach1Shard,
  create: boolean,
): Promise<FileSystemDirectoryHandle | null> {
  try {
    const ranges = await directory.getDirectoryHandle(RANGE_DIRECTORY, { create });
    return await ranges.getDirectoryHandle(shardRangeDirectoryName(shard), { create });
  } catch (error) {
    if (!create && isNotFoundError(error)) return null;
    throw error;
  }
}

async function removeShardRanges(
  directory: FileSystemDirectoryHandle,
  shard: Mach1Shard,
): Promise<void> {
  const ranges = await directory
    .getDirectoryHandle(RANGE_DIRECTORY)
    .catch((error: unknown) => {
      if (isNotFoundError(error)) return null;
      throw error;
    });
  if (!ranges) return;
  await ranges
    .removeEntry(shardRangeDirectoryName(shard), { recursive: true })
    .catch((error: unknown) => {
      if (!isNotFoundError(error)) throw error;
    });
}

async function inspectPersistedRanges(
  directory: FileSystemDirectoryHandle,
  shard: Mach1Shard,
): Promise<number> {
  const ranges = await shardRangeDirectory(directory, shard, false);
  if (!ranges) return 0;
  let savedBytes = 0;
  let missingSeen = false;
  let invalid = false;
  for (let offset = 0; offset < shard.byteCount; offset += DOWNLOAD_RANGE_BYTES) {
    const expectedLength = Math.min(DOWNLOAD_RANGE_BYTES, shard.byteCount - offset);
    const file = await ranges
      .getFileHandle(rangeFileName(offset))
      .then((handle) => handle.getFile())
      .catch((error: unknown) => {
        if (isNotFoundError(error)) return null;
        throw error;
      });
    if (!file) {
      missingSeen = true;
      continue;
    }
    if (missingSeen || file.size !== expectedLength) {
      invalid = true;
      break;
    }
    savedBytes += file.size;
  }
  if (invalid) {
    await removeShardRanges(directory, shard);
    return 0;
  }
  return savedBytes;
}

async function inventoryExistingShards(
  directory: FileSystemDirectoryHandle,
  manifest: Mach1WebManifest,
  onProgress?: ProgressCallback,
  signal?: AbortSignal,
): Promise<ExistingShardInventory> {
  const validFiles = new Set<string>();
  const partialBytes = new Map<string, number>();
  let validBytes = 0;
  let missingBytes = 0;
  let checkedBytes = 0;
  let needsRepair = false;
  for (const shard of manifest.split.shards) {
    signal?.throwIfAborted();
    const existing = await directory
      .getFileHandle(shard.file)
      .then((handle) => handle.getFile())
      .catch(() => null);
    if (existing?.size === shard.byteCount) {
      const digest = await hashBlob(existing, (shardBytes) => {
        onProgress?.({
          phase: "inventory",
          networkActive: false,
          shardIndex: shard.index,
          shardCount: manifest.split.shardCount,
          shardBytes,
          shardTotalBytes: shard.byteCount,
          totalBytes: checkedBytes + shardBytes,
          totalExpectedBytes: manifest.split.totalByteCount,
          detail: `Checking saved ${shard.file} locally — no model download`,
        });
      }, signal);
      checkedBytes += shard.byteCount;
      if (digest === shard.sha256) {
        validFiles.add(shard.file);
        validBytes += shard.byteCount;
        await removeShardRanges(directory, shard);
        continue;
      }
    }
    if (existing) await directory.removeEntry(shard.file).catch(() => undefined);
    needsRepair = true;
    const savedRangeBytes = await inspectPersistedRanges(directory, shard);
    if (savedRangeBytes > 0) partialBytes.set(shard.file, savedRangeBytes);
    missingBytes += shard.byteCount - savedRangeBytes;
  }
  return {
    validFiles,
    partialBytes,
    validBytes,
    savedBytes: validBytes + Array.from(partialBytes.values()).reduce((sum, bytes) => sum + bytes, 0),
    missingBytes,
    needsRepair,
  };
}

interface DownloadedShardAccounting {
  downloadedBytes: number;
  reusedRangeBytes: number;
}

async function storeRange(
  directory: FileSystemDirectoryHandle,
  shard: Mach1Shard,
  offset: number,
  bytes: Uint8Array,
): Promise<void> {
  const ranges = await shardRangeDirectory(directory, shard, true);
  if (!ranges) throw new Error(`Could not create durable range storage for shard ${shard.index}`);
  const name = rangeFileName(offset);
  const handle = await ranges.getFileHandle(name, { create: true });
  const writable = await handle.createWritable({ keepExistingData: false });
  try {
    await writable.write(Uint8Array.from(bytes));
    await writable.close();
  } catch (error) {
    await writable.abort().catch(() => undefined);
    await ranges.removeEntry(name).catch(() => undefined);
    throw error;
  }
  const committed = await handle.getFile();
  if (committed.size !== bytes.byteLength) {
    await ranges.removeEntry(name).catch(() => undefined);
    throw new Error(`Durable range checkpoint failed for shard ${shard.index} at ${offset}`);
  }
}

async function downloadVerifiedShard(
  directory: FileSystemDirectoryHandle,
  shard: Mach1Shard,
  persistedRangeBytes: number,
  totalBefore: number,
  totalExpected: number,
  onProgress?: ProgressCallback,
  signal?: AbortSignal,
  retryAfterHashMismatch = true,
): Promise<DownloadedShardAccounting> {
  let downloadedBytes = 0;
  for (let offset = persistedRangeBytes; offset < shard.byteCount; offset += DOWNLOAD_RANGE_BYTES) {
    signal?.throwIfAborted();
    const length = Math.min(DOWNLOAD_RANGE_BYTES, shard.byteCount - offset);
    const bytes = await fetchVerifiedRangeBytes(shard, offset, length, signal);
    await storeRange(directory, shard, offset, bytes);
    downloadedBytes += bytes.byteLength;
    onProgress?.({
      phase: "download",
      networkActive: true,
      shardIndex: shard.index,
      shardCount: 18,
      shardBytes: offset + bytes.byteLength,
      shardTotalBytes: shard.byteCount,
      totalBytes: totalBefore + offset + bytes.byteLength,
      totalExpectedBytes: totalExpected,
      detail: `${shard.file} · each completed 32 MiB range is saved for resume`,
    });
  }

  const ranges = await shardRangeDirectory(directory, shard, false);
  if (!ranges) throw new Error(`Durable ranges are missing for shard ${shard.index}`);
  const destination = await directory.getFileHandle(shard.file, { create: true });
  const writable = await destination.createWritable({ keepExistingData: false });
  const hash = sha256.create();
  let shardBytes = 0;
  try {
    for (let offset = 0; offset < shard.byteCount; offset += DOWNLOAD_RANGE_BYTES) {
      signal?.throwIfAborted();
      const expectedLength = Math.min(DOWNLOAD_RANGE_BYTES, shard.byteCount - offset);
      const part = await ranges.getFileHandle(rangeFileName(offset)).then((handle) => handle.getFile());
      if (part.size !== expectedLength) throw new Error(`Saved range size changed for shard ${shard.index}`);
      const reader = part.stream().getReader();
      try {
        for (;;) {
          signal?.throwIfAborted();
          const { value, done } = await reader.read();
          if (done) break;
          hash.update(value);
          await writable.write(Uint8Array.from(value));
          shardBytes += value.byteLength;
          onProgress?.({
            phase: "assemble",
            networkActive: false,
            shardIndex: shard.index,
            shardCount: 18,
            shardBytes,
            shardTotalBytes: shard.byteCount,
            totalBytes: totalBefore + shardBytes,
            totalExpectedBytes: totalExpected,
            detail: `Combining saved ranges for ${shard.file} locally — no model download`,
          });
        }
      } finally {
        reader.releaseLock();
      }
    }
    await writable.close();
  } catch (error) {
    await writable.abort().catch(() => undefined);
    await directory.removeEntry(shard.file).catch(() => undefined);
    throw error;
  }

  const digest = bytesToHex(hash.digest());
  if (shardBytes !== shard.byteCount || digest !== shard.sha256) {
    await directory.removeEntry(shard.file).catch(() => undefined);
    await removeShardRanges(directory, shard);
    if (retryAfterHashMismatch) {
      onProgress?.({
        phase: "preflight",
        networkActive: false,
        shardIndex: shard.index,
        shardCount: 18,
        shardBytes: 0,
        shardTotalBytes: shard.byteCount,
        totalBytes: totalBefore,
        totalExpectedBytes: totalExpected,
        detail: `A saved range for ${shard.file} failed the full SHA-256 check; retrying this shard from zero`,
      });
      const retried = await downloadVerifiedShard(
        directory,
        shard,
        0,
        totalBefore,
        totalExpected,
        onProgress,
        signal,
        false,
      );
      return {
        downloadedBytes: downloadedBytes + retried.downloadedBytes,
        reusedRangeBytes: 0,
      };
    }
    throw new Error(`Shard ${shard.index} failed exact SHA-256 verification after a clean retry`);
  }
  await removeShardRanges(directory, shard);
  return { downloadedBytes, reusedRangeBytes: persistedRangeBytes };
}

async function installMarker(
  directory: FileSystemDirectoryHandle,
  manifest: Mach1WebManifest,
): Promise<void> {
  const handle = await directory.getFileHandle("install.json", { create: true });
  const writable = await handle.createWritable({ keepExistingData: false });
  await writable.write(JSON.stringify({
    schemaVersion: 1,
    installedAt: new Date().toISOString(),
    manifestSha256: EXACT_MANIFEST_SHA256,
    modelSha256: manifest.unsplit.sha256,
    shardCount: manifest.split.shardCount,
    totalByteCount: manifest.split.totalByteCount,
  }));
  await writable.close();
}

export async function installExactMach1FromServer(
  onProgress?: ProgressCallback,
  signal?: AbortSignal,
): Promise<VerifiedMach1Installation> {
  const manifest = await fetchExactMach1Manifest(signal);
  const directory = await installDirectory(true);
  const inventory = await inventoryExistingShards(directory, manifest, onProgress, signal);
  if (inventory.needsRepair) {
    // A marker is a commit record, not a progress record. Invalidate it before
    // any repair mutation so a suspended/killed page can never leave a stale
    // "complete" installation behind.
    await removeInstallMarker(directory);
  }
  const assemblyOverheadBytes = manifest.split.shards
    .filter((shard) => !inventory.validFiles.has(shard.file))
    .reduce((largest, shard) => Math.max(largest, shard.byteCount), 0);
  await requireStorageCapacity(inventory.missingBytes + assemblyOverheadBytes);
  let totalCompletedBytes = 0;
  let downloadedByteCount = 0;
  let reusedRangeByteCount = 0;
  onProgress?.({
    phase: "preflight",
    networkActive: false,
    shardIndex: 0,
    shardCount: 18,
    shardBytes: 0,
    shardTotalBytes: 0,
    totalBytes: inventory.savedBytes,
    totalExpectedBytes: manifest.split.totalByteCount,
    detail: inventory.missingBytes === 0
      ? `Reusing ${formatProgressBytes(inventory.savedBytes)} already stored; no model bytes remain to download`
      : `Reusing ${formatProgressBytes(inventory.savedBytes)} already stored; downloading only ${formatProgressBytes(inventory.missingBytes)}`,
  });

  for (const shard of manifest.split.shards) {
    signal?.throwIfAborted();
    if (inventory.validFiles.has(shard.file)) {
      totalCompletedBytes += shard.byteCount;
      continue;
    }

    const result = await downloadVerifiedShard(
      directory,
      shard,
      inventory.partialBytes.get(shard.file) ?? 0,
      totalCompletedBytes,
      manifest.split.totalByteCount,
      onProgress,
      signal,
    );
    downloadedByteCount += result.downloadedBytes;
    reusedRangeByteCount += result.reusedRangeBytes;
    totalCompletedBytes += shard.byteCount;
  }

  const installation = await readVerifiedMach1Files(manifest, directory, onProgress, signal);
  // Commit last, after every just-written shard has been reopened and hashed.
  await installMarker(directory, manifest);
  installation.accounting = {
    downloadedByteCount,
    reusedVerifiedByteCount: inventory.validBytes + reusedRangeByteCount,
    duplicateCopyByteCount: 0,
    verifiedFileNames: installation.files.map((file) => file.name),
  };
  onProgress?.({
    phase: "complete",
    networkActive: false,
    shardIndex: 18,
    shardCount: 18,
    shardBytes: 0,
    shardTotalBytes: 0,
    totalBytes: manifest.split.totalByteCount,
    totalExpectedBytes: manifest.split.totalByteCount,
    detail: "All 18 exact shards verified in OPFS — model network idle",
  });
  return installation;
}

async function openCommittedInstallation(signal?: AbortSignal): Promise<{
  manifest: Mach1WebManifest;
  directory: FileSystemDirectoryHandle;
}> {
  const manifest = await fetchExactMach1Manifest(signal);
  const directory = await installDirectory(false);
  const marker = await requireStoredFile(directory, "install.json");
  let markerValue: Record<string, unknown>;
  try {
    markerValue = JSON.parse(await marker.text()) as Record<string, unknown>;
  } catch {
    throw incompleteInstallation("a valid install.json completion marker");
  }
  if (
    markerValue.manifestSha256 !== EXACT_MANIFEST_SHA256 ||
    markerValue.modelSha256 !== manifest.unsplit.sha256 ||
    markerValue.shardCount !== 18 ||
    markerValue.totalByteCount !== manifest.split.totalByteCount
  ) {
    throw new Error("Stored MACH-1 installation marker does not match the pinned release");
  }
  return { manifest, directory };
}

export async function openVerifiedMach1Installation(
  onProgress?: ProgressCallback,
  signal?: AbortSignal,
): Promise<VerifiedMach1Installation> {
  const { manifest, directory } = await openCommittedInstallation(signal);
  return readVerifiedMach1Files(manifest, directory, onProgress, signal);
}

/**
 * Reopens fresh Safari/WebKit `File` snapshots without performing a duplicate
 * site hash pass. The frozen runtime immediately performs the authoritative
 * full SHA-256 verification before it can create or use the model.
 */
export async function openExactMach1InstallationForRuntimeVerification(
  signal?: AbortSignal,
): Promise<RuntimeVerificationMach1Installation> {
  const { manifest, directory } = await openCommittedInstallation(signal);
  const files: File[] = [];
  let totalBytes = 0;
  for (const shard of manifest.split.shards) {
    signal?.throwIfAborted();
    const file = await requireStoredFile(directory, shard.file);
    if (file.size !== shard.byteCount) throw new Error(`Stored shard ${shard.index} size mismatch`);
    files.push(file);
    totalBytes += file.size;
  }
  return {
    manifest,
    files,
    manifestSha256: EXACT_MANIFEST_SHA256,
    verification: "deferred-to-exact-runtime",
    accounting: {
      downloadedByteCount: 0,
      reusedVerifiedByteCount: totalBytes,
      duplicateCopyByteCount: 0,
      verifiedFileNames: files.map((file) => file.name),
    },
  };
}

async function readVerifiedMach1Files(
  manifest: Mach1WebManifest,
  directory: FileSystemDirectoryHandle,
  onProgress?: ProgressCallback,
  signal?: AbortSignal,
): Promise<VerifiedMach1Installation> {
  const files: File[] = [];
  let totalBytes = 0;
  for (const shard of manifest.split.shards) {
    signal?.throwIfAborted();
    const file = await requireStoredFile(directory, shard.file);
    if (file.size !== shard.byteCount) throw new Error(`Stored shard ${shard.index} size mismatch`);
    const digest = await hashBlob(file, (shardBytes) => {
      onProgress?.({
        phase: "verify",
        networkActive: false,
        shardIndex: shard.index,
        shardCount: 18,
        shardBytes,
        shardTotalBytes: shard.byteCount,
        totalBytes: totalBytes + shardBytes,
        totalExpectedBytes: manifest.split.totalByteCount,
        detail: `Final SHA-256 pass over ${shard.file} locally — no model download`,
      });
    }, signal);
    if (digest !== shard.sha256) throw new Error(`Stored shard ${shard.index} SHA-256 mismatch`);
    totalBytes += shard.byteCount;
    files.push(file);
  }
  return {
    manifest,
    files,
    manifestSha256: EXACT_MANIFEST_SHA256,
    verification: "site-sha256",
    accounting: {
      downloadedByteCount: 0,
      reusedVerifiedByteCount: totalBytes,
      duplicateCopyByteCount: 0,
      verifiedFileNames: files.map((file) => file.name),
    },
  };
}

export async function installExactMach1FromFiles(
  selected: Iterable<File>,
  onProgress?: ProgressCallback,
  signal?: AbortSignal,
): Promise<VerifiedMach1Installation> {
  const manifest = await fetchExactMach1Manifest(signal);
  const selectedByName = new Map(Array.from(selected, (file) => [file.name, file] as const));
  if (selectedByName.size !== 18) throw new Error("Select exactly the 18 pinned MACH-1 split GGUF files");
  await requireStorageCapacity(manifest.split.totalByteCount);
  const directory = await installDirectory(true);
  await removeInstallMarker(directory);
  let totalBytes = 0;
  for (const shard of manifest.split.shards) {
    const source = selectedByName.get(shard.file);
    if (!source || source.size !== shard.byteCount) throw new Error(`Missing or wrong-sized shard ${shard.index}`);
    const digest = await hashBlob(source, (shardBytes) => {
      onProgress?.({
        phase: "verify",
        networkActive: false,
        shardIndex: shard.index,
        shardCount: 18,
        shardBytes,
        shardTotalBytes: shard.byteCount,
        totalBytes: totalBytes + shardBytes,
        totalExpectedBytes: manifest.split.totalByteCount,
        detail: `Checking selected ${shard.file} locally — no model download`,
      });
    }, signal);
    if (digest !== shard.sha256) throw new Error(`Selected shard ${shard.index} SHA-256 mismatch`);
    const destination = await directory.getFileHandle(shard.file, { create: true });
    const writable = await destination.createWritable({ keepExistingData: false });
    await source.stream().pipeTo(writable, { signal });
    totalBytes += shard.byteCount;
  }
  const installation = await readVerifiedMach1Files(manifest, directory, onProgress, signal);
  await installMarker(directory, manifest);
  installation.accounting = {
    downloadedByteCount: totalBytes,
    reusedVerifiedByteCount: 0,
    duplicateCopyByteCount: 0,
    verifiedFileNames: installation.files.map((file) => file.name),
  };
  return installation;
}
